import { format, startOfMonth, startOfWeek, parse } from 'date-fns'
import DatePicker from 'react-datepicker'

interface DateRangePickerProps {
  startDate: string
  endDate: string
  onChange: (start: string, end: string) => void
}

type PresetDays = { label: string; days: number }
type PresetValue = { label: string; value: 'week' | 'month' }
type Preset = PresetDays | PresetValue

const presets: Preset[] = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 Days', days: 6 },
  { label: 'Last 30 Days', days: 29 },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
]

// Helper to get UTC date string (yyyy-MM-dd)
const getUtcDateString = (date: Date): string => date.toISOString().split('T')[0]

// Helper to get UTC date N days ago
const getUtcDaysAgo = (days: number): string => {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
  return getUtcDateString(date)
}

export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
}: DateRangePickerProps) {
  const now = new Date()
  // Use UTC for "today" since backend stores timestamps in UTC
  const utcToday = getUtcDateString(now)
  // Local today for maxDate constraint on the date picker UI
  const localToday = now

  const handlePresetClick = (preset: Preset) => {
    const end = utcToday

    if ('days' in preset) {
      const start = getUtcDaysAgo(preset.days)
      onChange(start, end)
    } else if (preset.value === 'week') {
      const start = format(startOfWeek(now), 'yyyy-MM-dd')
      onChange(start, end)
    } else if (preset.value === 'month') {
      const start = format(startOfMonth(now), 'yyyy-MM-dd')
      onChange(start, end)
    }
  }

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'), endDate)
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      onChange(startDate, format(date, 'yyyy-MM-dd'))
    }
  }

  const startDateObj = parse(startDate, 'yyyy-MM-dd', new Date())
  const endDateObj = parse(endDate, 'yyyy-MM-dd', new Date())

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2">
        <DatePicker
          selected={startDateObj}
          onChange={handleStartDateChange}
          maxDate={endDateObj}
          dateFormat="yyyy-MM-dd"
          className="input"
          aria-label="Start date"
        />
        <span className="text-text-secondary" aria-hidden="true">to</span>
        <DatePicker
          selected={endDateObj}
          onChange={handleEndDateChange}
          minDate={startDateObj}
          maxDate={localToday}
          dateFormat="yyyy-MM-dd"
          className="input"
          aria-label="End date"
        />
      </div>

      <div className="flex gap-1">
        {presets.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePresetClick(preset)}
            className="px-2 py-1 text-xs font-medium text-text-secondary bg-noir-tertiary rounded-sm hover:bg-noir-tertiary focus:outline-hidden focus:ring-2 focus:ring-accent-cyan focus:ring-offset-1 transition-colors"
            aria-label={`Set date range to ${preset.label.toLowerCase()}`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}
